/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, {
  forwardRef,
  useRef,
  useEffect,
  useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import findIndex from 'lodash/findIndex';
import useScrollBarWidth from 'utils/scroll-bar-width';
import Logo from 'components/Icons/Logo/Logo';
import Menu from './Menu/Menu';
import Contact from './Contact/Contact';
import Search from './Search/Search';
import './Submenu.scss';

/**
 * This is the Submenu component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
const Submenu = forwardRef(
  (
    {
      breakpoint,
      home,
      search,
      contact,
      menu,
      about,
      privacy,
      languages,
      active,
      onFocus,
      onBlur,
      onSearchClick,
      onContactClick,
      onMenuClick,
      onClose,
      labels,
    },
    ref
  ) => {
    const container = useRef(null);
    const scrollBarWidth = useScrollBarWidth();

    useEffect(() => {
      if (breakpoint === 'desktop' && scrollBarWidth > 0) {
        if (active) {
          container.current.style.width = `calc((100% - ${scrollBarWidth}px) * 0.6)`;
        } else {
          container.current.style.width = `calc(60% - ${scrollBarWidth}px)`;
        }
      } else {
        container.current.style.width = null;
      }
    }, [breakpoint, active, scrollBarWidth]);

    useEffect(() => {
      function onFocusIn({ target }) {
        if (!container.current.contains(target)) {
          onBlur();
        }
      }

      if (active) {
        document.body.addEventListener('focusin', onFocusIn);

        return function cleanup() {
          document.body.removeEventListener('focusin', onFocusIn);
        };
      }
    }, [active, onBlur]);

    useImperativeHandle(ref, () => ({
      focusOnFirstItem: () => {
        const first = container.current.querySelector(
          '.content-menu [role="menuitem"]'
        );

        if (first) {
          first.focus();
        }
      },

      focusOnLastItem: () => {
        const items = container.current.querySelectorAll(
          '.content-menu [role="menuitem"]'
        );

        const last = items[items.length - 1];
        if (last) {
          last.focus();
        }
      },

      focusOnNextItem: () => {
        let current = -1;
        const items = container.current.querySelectorAll(
          '.content-menu [role="menuitem"], header button'
        );

        Array.from(items).forEach((item, i) => {
          if (item === document.activeElement) {
            current = i;
          }
        });

        current += 1;

        if (current >= items.length) {
          current = 0;
        }

        if (items[current]) {
          items[current].focus();
        }
      },

      focusOnPrevItem: () => {
        let current = -1;
        const items = container.current.querySelectorAll(
          '.content-menu [role="menuitem"], header button'
        );

        Array.from(items).forEach((item, i) => {
          if (item === document.activeElement) {
            current = i;
          }
        });

        current -= 1;

        if (current < 0) {
          current = items.length - 1;
        }

        if (items[current]) {
          items[current].focus();
        }
      },

      focusOnLetter: letter => {
        const items = container.current.querySelectorAll(
          '.content-menu [role="menuitem"], header button'
        );

        const filtered = Array.from(items).filter(node => {
          return node.textContent.substring(0, 1).toLowerCase() == letter;
        });

        let index = findIndex(filtered, node => {
          return node == document.activeElement;
        });

        index = index + 1 > filtered.length - 1 ? 0 : index + 1;

        if (filtered[index]) {
          filtered[index].focus();
        }
      },
    }));

    function renderTop() {
      if (breakpoint !== null) {
        return (
          <header className="page-padding">
            <button
              tabIndex={active ? 0 : -1}
              className="close"
              onClick={onClose}
              onFocus={() => {
                onFocus();
              }}
            >
              {menu.close}
            </button>
            <Logo />
            <div>
              <button
                tabIndex={active ? 0 : -1}
                className={classnames('search', {
                  active: active === 'search',
                })}
                onClick={onSearchClick}
              >
                {search.title}
              </button>
              <button
                tabIndex={active ? 0 : -1}
                className={classnames('contact', {
                  active: active === 'contact',
                })}
                onClick={onContactClick}
              >
                {contact.title}
              </button>
              <button
                tabIndex={active ? 0 : -1}
                className={classnames('menu', { active: active === 'menu' })}
                onClick={onMenuClick}
              >
                {menu.title}
              </button>
            </div>
          </header>
        );
      }

      return (
        <header className="page-padding">
          <div className="top">
            <a
              className="home"
              href={home.href}
              title={home.title}
              tabIndex={active ? 0 : -1}
            >
              {home.text}
            </a>
            <Logo />
            <button
              className="close"
              onClick={onClose}
              tabIndex={active ? 0 : -1}
            >
              {menu.close}
            </button>
          </div>
          <div className="sub" role="tablist">
            <button
              id="menubutton"
              role="tab"
              aria-selected={active === 'menu'}
              aria-controls="menu"
              tabIndex={active ? 0 : -1}
              className={classnames({ active: active === 'menu' })}
              onClick={onMenuClick}
            >
              {menu.title}
            </button>
            <button
              id="contactbutton"
              role="tab"
              aria-selected={active === 'contact'}
              aria-controls="contact"
              tabIndex={active ? 0 : -1}
              className={classnames({ active: active === 'contact' })}
              onClick={onContactClick}
            >
              {contact.title}
            </button>
            <button
              id="searchbutton"
              role="tab"
              aria-selected={active === 'search'}
              aria-controls="search"
              tabIndex={active ? 0 : -1}
              className={classnames({ active: active === 'search' })}
              onClick={onSearchClick}
            >
              {search.title}
            </button>
          </div>
        </header>
      );
    }

    return (
      <nav
        ref={container}
        className={classnames('submenu')}
        aria-label={labels.title}
        aria-hidden={!active}
      >
        {renderTop()}
        <Menu
          {...menu}
          open={active === 'menu'}
          about={about}
          privacy={privacy}
          languages={languages}
          onFocus={onFocus}
          labels={labels.menu}
        />
        <Contact
          open={active === 'contact'}
          {...contact}
          onFocus={onFocus}
          labels={labels.contact}
        />
        <Search
          open={active === 'search'}
          {...search}
          onFocus={onFocus}
          labels={labels.search}
        />
      </nav>
    );
  }
);

// Submenu = forwardRef(Submenu);

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Submenu.propTypes = {
  breakpoint: PropTypes.string,

  active: PropTypes.string,

  home: PropTypes.shape({
    href: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
  }).isRequired,

  search: PropTypes.shape({
    title: PropTypes.string.isRequired,
  }).isRequired,

  contact: PropTypes.shape({
    title: PropTypes.string.isRequired,
  }).isRequired,

  about: PropTypes.object.isRequired,

  privacy: PropTypes.object.isRequired,

  menu: PropTypes.shape({
    title: PropTypes.string.isRequired,
    close: PropTypes.string.isRequired,
  }).isRequired,

  languages: PropTypes.array.isRequired,

  onFocus: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  onSearchClick: PropTypes.func.isRequired,
  onContactClick: PropTypes.func.isRequired,
  onMenuClick: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  labels: PropTypes.shape({
    title: PropTypes.string.isRequired,
    menu: PropTypes.object.isRequired,
    contact: PropTypes.object.isRequired,
    search: PropTypes.object,
  }).isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Submenu.defaultProps = {
  languages: [],
  active: null,
  onFocus: () => {},
  onBlur: () => {},
  onSearchClick: () => {},
  onContactClick: () => {},
  onMenuClick: () => {},
  onClose: () => {},
  labels: {
    title: 'Menu',
    menu: {
      languages: 'Languages',
    },
    contact: {
      title: 'Contact',
      address: 'Address',
      phone: 'Phone',
      email: 'Email',
      socials: 'Social medias',
      employees: 'Employees',
    },
  },
};

export default Submenu;
